import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, FileText, Activity } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chart styling constants
const CHART_COLORS = {
  primary: '#5A7D6C',
  grid: '#E5E2DC',
  text: '#718096'
};

const LINE_DOT_CONFIG = { fill: CHART_COLORS.primary, r: 4 };
const Y_AXIS_DOMAIN = [0, 4];

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [patientAnalytics, setPatientAnalytics] = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchPatients();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const { data } = await axios.get(`${API}/analytics/dashboard`, { withCredentials: true });
      setDashboardData(data);
    } catch (error) {
      toast.error('Error al cargar analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const { data } = await axios.get(`${API}/patients`, { withCredentials: true });
      setPatients(data);
    } catch (error) {
      console.error('Error loading patients:', error);
      toast.error('Error al cargar pacientes');
    }
  };

  const fetchPatientAnalytics = async (patientId) => {
    if (!patientId) return;
    
    setLoadingPatient(true);
    try {
      const { data } = await axios.get(`${API}/analytics/patient/${patientId}`, { withCredentials: true });
      setPatientAnalytics(data);
    } catch (error) {
      toast.error('Error al cargar análisis del paciente');
    } finally {
      setLoadingPatient(false);
    }
  };

  const handlePatientChange = (patientId) => {
    setSelectedPatient(patientId);
    fetchPatientAnalytics(patientId);
  };

  if (loading) return <div>Cargando...</div>;

  const COLORS = ['#5A7D6C', '#D98A6C', '#E5E2DC', '#718096'];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
          Analytics y Estadísticas
        </h1>
        <p className="text-sm mt-1" style={{ color: '#718096' }}>
          Visualiza el progreso y estadísticas de tus pacientes
        </p>
      </div>

      {/* Dashboard Overview */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#718096' }}>Total Pacientes</p>
                <p className="text-3xl font-semibold mt-2" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  {dashboardData.overview.total_patients}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#5A7D6C20' }}>
                <Users size={24} style={{ color: '#5A7D6C' }} />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#718096' }}>Informes Totales</p>
                <p className="text-3xl font-semibold mt-2" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  {dashboardData.overview.total_reports}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#D98A6C20' }}>
                <FileText size={24} style={{ color: '#D98A6C' }} />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#718096' }}>Últimos 30 días</p>
                <p className="text-3xl font-semibold mt-2" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  {dashboardData.overview.recent_reports_30d}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#5A7D6C20' }}>
                <TrendingUp size={24} style={{ color: '#5A7D6C' }} />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#718096' }}>Próximas Citas (7d)</p>
                <p className="text-3xl font-semibold mt-2" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  {dashboardData.overview.upcoming_appointments_7d}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#D98A6C20' }}>
                <Activity size={24} style={{ color: '#D98A6C' }} />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Patient Analysis */}
      <Card className="p-8">
        <h2 className="text-xl font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
          Análisis por Paciente
        </h2>
        
        <div className="mb-6">
          <Label>Seleccionar Paciente</Label>
          <Select value={selectedPatient} onValueChange={handlePatientChange}>
            <SelectTrigger className="w-full mt-2">
              <SelectValue placeholder="Selecciona un paciente..." />
            </SelectTrigger>
            <SelectContent>
              {patients.map((patient) => (
                <SelectItem key={patient.id} value={patient.id}>
                  {patient.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loadingPatient && (
          <div className="text-center py-8" style={{ color: '#718096' }}>
            Cargando análisis...
          </div>
        )}

        {patientAnalytics && !loadingPatient && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F9F8F6' }}>
                <p className="text-xs font-medium" style={{ color: '#718096' }}>Sesiones Totales</p>
                <p className="text-2xl font-semibold mt-1" style={{ color: '#2D3748' }}>
                  {patientAnalytics.summary.total_sessions}
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F9F8F6' }}>
                <p className="text-xs font-medium" style={{ color: '#718096' }}>Citas Programadas</p>
                <p className="text-2xl font-semibold mt-1" style={{ color: '#2D3748' }}>
                  {patientAnalytics.summary.total_appointments}
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F9F8F6' }}>
                <p className="text-xs font-medium" style={{ color: '#718096' }}>Citas Completadas</p>
                <p className="text-2xl font-semibold mt-1" style={{ color: '#2D3748' }}>
                  {patientAnalytics.summary.completed_appointments}
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F9F8F6' }}>
                <p className="text-xs font-medium" style={{ color: '#718096' }}>Tasa de Asistencia</p>
                <p className="text-2xl font-semibold mt-1" style={{ color: '#2D3748' }}>
                  {patientAnalytics.summary.attendance_rate}%
                </p>
              </div>
            </div>

            {/* Acceptance Trend */}
            {patientAnalytics.trends.acceptance.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  Evolución de Aceptación Promedio
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={patientAnalytics.trends.acceptance}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" stroke={CHART_COLORS.text} />
                    <YAxis stroke={CHART_COLORS.text} domain={Y_AXIS_DOMAIN} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke={CHART_COLORS.primary}
                      strokeWidth={2}
                      name="Nivel de Aceptación"
                      dot={LINE_DOT_CONFIG}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Detailed Acceptance Metrics */}
            {patientAnalytics.trends.acceptance_detail && patientAnalytics.trends.acceptance_detail.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  Métricas de Aceptación Detalladas
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={patientAnalytics.trends.acceptance_detail}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" stroke={CHART_COLORS.text} />
                    <YAxis stroke={CHART_COLORS.text} domain={[0, 4]} ticks={[1, 2, 3, 4]} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="Acepta el espacio" 
                      stroke="#5A7D6C"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="Acepta la propuesta" 
                      stroke="#D98A6C"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="Interactúa con el profesional" 
                      stroke="#718096"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="Sostiene actividad" 
                      stroke="#E5E2DC"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-2 text-xs text-gray-500 text-center">
                  Escala: 1 = No/0-25%, 2 = 25-50%, 3 = 50-75%, 4 = 75-100%
                </div>
              </div>
            )}

            {/* Behavior Metrics */}
            {patientAnalytics.trends.behavior_metrics && patientAnalytics.trends.behavior_metrics.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  Duración de Sesiones
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={patientAnalytics.trends.behavior_metrics}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" stroke={CHART_COLORS.text} />
                    <YAxis stroke={CHART_COLORS.text} label={{ value: 'Minutos', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    <Bar 
                      dataKey="duration" 
                      fill={CHART_COLORS.primary}
                      name="Duración (minutos)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Emotional States Trend */}
            {patientAnalytics.trends.emotional_states.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  Estados Emocionales Positivos
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={patientAnalytics.trends.emotional_states}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" stroke={CHART_COLORS.text} />
                    <YAxis stroke={CHART_COLORS.text} domain={[0, 100]} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="positive_percentage" 
                      stroke="#D98A6C" 
                      strokeWidth={2}
                      name="% Emociones Positivas"
                      dot={{ fill: '#D98A6C', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Skills Progress */}
            {patientAnalytics.trends.skills_progress.motor.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                  Progreso de Habilidades
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={patientAnalytics.trends.skills_progress.motor}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" stroke={CHART_COLORS.text} />
                    <YAxis stroke={CHART_COLORS.text} domain={[1, 4]} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      data={patientAnalytics.trends.skills_progress.motor}
                      stroke={CHART_COLORS.primary} 
                      strokeWidth={2}
                      name="Habilidades Motoras"
                      dot={{ fill: '#5A7D6C', r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      data={patientAnalytics.trends.skills_progress.processing}
                      stroke="#D98A6C" 
                      strokeWidth={2}
                      name="Procesamiento"
                      dot={{ fill: '#D98A6C', r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      data={patientAnalytics.trends.skills_progress.social}
                      stroke={CHART_COLORS.text} 
                      strokeWidth={2}
                      name="Habilidades Sociales"
                      dot={{ fill: '#718096', r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {!selectedPatient && !loadingPatient && (
          <div className="text-center py-12" style={{ color: '#718096' }}>
            <Activity size={48} className="mx-auto mb-4 opacity-50" />
            <p>Selecciona un paciente para ver su análisis detallado</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AnalyticsPage;
